// Wire-contract types shared by the SPA and the Functions backend, plus
// hand-written parse/guard functions the SPA uses to validate `fetch().json()`
// at the boundary. Turning `unknown` into a typed shape (or throwing) means a
// server contract change fails loudly here instead of crashing deep in render
// code. No runtime dependency — the app ships zero bundled deps.

import type { PingLike, DeviceCapabilities } from './crypto';
import { b64urlDecode } from './crypto';

// Re-export so the SPA can import the advertised-capabilities shape from the
// wire module alongside the command types that reference it.
export type { DeviceCapabilities } from './crypto';

// A stored/transported ping. Same shape the canonicaliser signs over.
export type Ping = PingLike;

// A per-ping authenticated data payload (ADR 0023): exactly PING_DATA_BYTES raw
// bytes, carried/canonicalised as base64url (no padding). The SINGLE rule home,
// imported by the ingest (`pings.ts`) so the length check isn't duplicated.
// AUTHENTICATION only — the bytes ride the existing ping signature but are NOT
// encrypted; any renderer MUST treat them as untrusted (like the device tag).
export const PING_DATA_BYTES = 64;
export function isValidPingData(v: unknown): v is string {
  if (typeof v !== 'string' || v.length === 0) return false;
  try {
    return b64urlDecode(v).length === PING_DATA_BYTES;
  } catch {
    return false;
  }
}

export interface DeviceCreated {
  token: string;
  created_at: number;
}
export interface DevicePings {
  pings: Ping[];
  created_at: number;
  // The device's display tag (ADR 0022); null when unset. Additive to the
  // ADR-0020 `{ pings, created_at }` self-read shape — `created_at` (web
  // self-dedup, reconcileCreatedAt) is preserved.
  tag: string | null;
  // Server→client messaging piggyback (ADR 0024). The owner self-read carries
  // pending query/command items the device is expected to execute and ack via
  // `POST /devices/:token/command-results`. An empty array when none. The
  // cohort, world, and share reads NEVER include this field — it is the
  // privacy invariant that keeps the channel owner-only. Additive: an
  // un-updated client (Gson ignores unknown keys; the SPA's `strOrNull`-style
  // tolerant parser absorbs an absent field as `[]`) keeps working.
  pending_commands: PendingCommand[];
  // Geofence distribution piggyback (ADR 0026): the device's group's live fence
  // shapes, emitted ONLY on the device-key-signed read — the twin of
  // pending_commands, but DEVICE-KEY path only (stricter: a public-visibility
  // device can be in a claimed group, and the public read is unsigned, so the
  // fences must never ride it). Absent on the public/session/cohort/world/share
  // reads ⇒ []. The device evaluates these locally against its own exact fix and
  // reports breaches via POST /devices/:token/geofence-events.
  geofences: Geofence[];
}
// A single enqueued query/command piggybacked on the owner self-read (ADR
// 0024). The device executes the work and posts the result back via
// `POST /devices/:token/command-results`, binding `command_id === id`. `tier`
// echoes the device-advertised tier the owner enqueued under; the server
// rejected the enqueue if the device hadn't advertised it. `args` is the
// operator-bounded JSON-shaped string the owner sent (or null when none); the
// device parses it in its own kind handler.
export interface PendingCommand {
  id: number;
  tier: 'query' | 'command';
  kind: string;
  args: string | null;
}
export interface PingAccepted {
  accepted: number;
}
export interface SessionStarted {
  session_id: number;
  started_at: number;
}
export interface SessionStopped {
  session_id: number;
  ended_at: number;
}
export interface ShareMinted {
  share_token: string;
  expires_at: number | null;
}
export interface ShareMeta {
  share_token: string;
  range: string;
  precision: string;
  expires_at: number | null;
  created_at: number;
}
export interface ShareScopeEcho {
  range: string;
  precision: string;
  expires_at: number | null;
}
export interface SharePings {
  pings: Ping[];
  share_scope: ShareScopeEcho;
  since: number;
  now: number;
}
// `animals` / `group` are opaque label / group-key segment arrays (ADR
// 0011) — the field names are kept for the animal SPA, but the backend
// no longer constrains them to a word list. Stored in `devices.label` /
// `devices.group_key`.
// Per-device cohort scope echoed by /recent (Phase 2). Always present (a
// NULL-scope device echoes the effective defaults {all, exact}); `range` and
// `precision` are left as plain strings here — the same loose typing the wire
// uses for share scopes — and narrowed by callers as needed.
export interface CohortScopeEcho {
  range: string;
  precision: string;
}
export interface RemoteCohortDevice {
  token: string;
  animals: string[];
  created_at: number;
  pings: Ping[];
  truncated: boolean;
  cohort_scope: CohortScopeEcho;
  // The device's display tag (ADR 0022); null when unset.
  tag: string | null;
}
// Response of POST /api/v1/devices/:token/cohort-scope (the signed setter).
export interface CohortScopeSet {
  cohort_scope: CohortScopeEcho;
}
// Response shapes for the server→client messaging channel (ADR 0024).
//   POST /api/v1/devices/:token/capabilities — device-signed advertisement.
//   POST /api/v1/devices/:token/commands     — owner-session-gated enqueue.
//   POST /api/v1/devices/:token/command-results — device-signed result-return.
// All three are tight echoes — no extra surface, no list endpoints in V1 (an
// operator who wants to observe queue depth uses /admin/stats).
export interface CapabilitiesSet {
  capabilities: { query: boolean; command: boolean };
}
export interface CommandEnqueued {
  id: number;
  expires_at: number;
}
export interface CommandResultAccepted {
  acked_at: number;
}
// One row of the durable command/response history served by the owner-session-
// gated read `GET /api/v1/devices/:token/commands` (web command-console spec,
// rev 3). Mirrors `device_commands` 1:1 on the server clock: `issued_at` is when
// the operator enqueued, `acked_at`/`result_data` are null while pending and set
// together when the device returns a result. Distinct from `PendingCommand` (the
// device-key piggyback shape, which omits the result/lifecycle fields).
export interface CommandResult {
  id: number;
  tier: 'query' | 'command';
  kind: string;
  args: string | null;
  issued_at: number;
  expires_at: number;
  acked_at: number | null;
  result_data: string | null;
}
// Response of `GET /api/v1/devices/:token/commands` — the device's advertised
// tiers (drives the console dropdown; null when never advertised) plus the
// windowed command/response history (drives the live feed AND the replay
// overlay), oldest first.
export interface DeviceCommandsView {
  capabilities: DeviceCapabilities | null;
  commands: CommandResult[];
}
// Response of POST /api/v1/devices/:token/tag (the signed display-tag setter,
// ADR 0022). Echoes the stored value (null when cleared).
export interface DeviceTagSet {
  tag: string | null;
}

// A device display tag (ADR 0022) — a short, human-friendly name layered over
// the immutable token-derived label/animals. The SINGLE rule home, imported by
// the SPA (client guard) and the ingest (`tag.ts`) so the two cannot drift.
export const MAX_DEVICE_TAG_LEN = 32;
// A SET value is a string of 1..MAX_DEVICE_TAG_LEN UTF-16 units, free of
// control characters (a display name is single-line). Clearing the tag is `''`
// / `null` and is handled by the caller (normalised to null), NOT validated
// here. The renderer still escapes the tag — this only keeps junk out of the DB.
export function isValidDeviceTag(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  if (v.length < 1 || v.length > MAX_DEVICE_TAG_LEN) return false;
  for (let i = 0; i < v.length; i++) {
    const c = v.charCodeAt(i);
    if (c <= 0x1f || (c >= 0x7f && c <= 0x9f)) return false;
  }
  return true;
}

// Group markers + geofences bounds (ADR 0026). A marker/geofence label is a
// control-char-free single-line string, longer than a device tag because a
// marker label doubles as low-frequency indirect messaging. The SINGLE rule
// home, imported by the SPA (client guards) and the group endpoints (server
// validation) so the two cannot drift — same discipline as MAX_DEVICE_TAG_LEN.
export const MARKER_LABEL_LEN = 80;
export const MARKER_KIND_LEN = 32;
export const MAX_MARKERS_PER_GROUP = 200;

// A control-char-free single-line string of 1..max UTF-16 units (the shared
// shape behind isValidDeviceTag). An empty/cleared value is the caller's
// concern (normalised to null), NOT validated here.
function isControlFreeString(v: unknown, max: number): v is string {
  if (typeof v !== 'string') return false;
  if (v.length < 1 || v.length > max) return false;
  for (let i = 0; i < v.length; i++) {
    const c = v.charCodeAt(i);
    if (c <= 0x1f || (c >= 0x7f && c <= 0x9f)) return false;
  }
  return true;
}
// A marker/geofence LABEL value (optional): a non-empty value must be
// control-char-free and <= MARKER_LABEL_LEN. Clearing it is ''/null and is the
// caller's concern (normalised to null), like the device tag.
export function isValidMarkerLabel(v: unknown): v is string {
  return isControlFreeString(v, MARKER_LABEL_LEN);
}
// A marker/geofence KIND value (required): control-char-free, <= MARKER_KIND_LEN.
export function isValidMarkerKind(v: unknown): v is string {
  return isControlFreeString(v, MARKER_KIND_LEN);
}
// Coordinate range guards, shared by markers and (Phase 2) geofence shapes.
export function isValidLat(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= -90 && v <= 90;
}
export function isValidLng(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= -180 && v <= 180;
}

// Geofence shape bounds (ADR 0026). A shape is a circle XOR a polygon; the caps
// bound the JSON that rides the device-key /pings piggyback.
export const MAX_GEOFENCES_PER_GROUP = 32;
export const MIN_POLYGON_VERTICES = 3; // a polygon needs at least a triangle
export const MAX_POLYGON_VERTICES = 64;
export const MAX_GEOFENCE_RADIUS_M = 1_000_000; // 1000 km — generous upper bound

// A geofence shape: a circle XOR a polygon. The device evaluates a fix against
// this locally (ADR 0026 decision 2) — the server only stores + bounds it.
export interface GeofenceCircle {
  circle: { lat: number; lng: number; radius_m: number };
}
export interface GeofencePolygon {
  polygon: [number, number][]; // [[lat, lng], …]
}
export type GeofenceShape = GeofenceCircle | GeofencePolygon;

// Validate AND normalize a geofence shape: returns the canonical shape (extra
// keys stripped) or null if invalid. The SINGLE rule home — the server setter
// validates with this then JSON.stringifies the result for storage, and the SPA
// uses it as a client guard. Exactly one of circle/polygon; coordinates in
// range; radius positive + bounded; polygon vertex count in [MIN..MAX].
export function parseGeofenceShape(v: unknown): GeofenceShape | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const hasCircle = o.circle !== undefined;
  const hasPolygon = o.polygon !== undefined;
  if (hasCircle === hasPolygon) return null; // exactly one (XOR)
  if (hasCircle) {
    const c = o.circle;
    if (!c || typeof c !== 'object' || Array.isArray(c)) return null;
    const cc = c as Record<string, unknown>;
    if (!isValidLat(cc.lat) || !isValidLng(cc.lng)) return null;
    if (
      typeof cc.radius_m !== 'number' ||
      !Number.isFinite(cc.radius_m) ||
      cc.radius_m <= 0 ||
      cc.radius_m > MAX_GEOFENCE_RADIUS_M
    ) {
      return null;
    }
    return { circle: { lat: cc.lat, lng: cc.lng, radius_m: cc.radius_m } };
  }
  const p = o.polygon;
  if (!Array.isArray(p) || p.length < MIN_POLYGON_VERTICES || p.length > MAX_POLYGON_VERTICES) {
    return null;
  }
  const verts: [number, number][] = [];
  for (const pt of p) {
    if (!Array.isArray(pt) || pt.length !== 2) return null;
    const lat = pt[0];
    const lng = pt[1];
    if (!isValidLat(lat) || !isValidLng(lng)) return null;
    verts.push([lat, lng]);
  }
  return { polygon: verts };
}
export interface GroupRecent {
  group: string[];
  since: number;
  now: number;
  devices: RemoteCohortDevice[];
  truncated: boolean;
}

// World view (GET /api/v1/world/active). One row per device — its LATEST
// in-window position, not a trail. `id` is the opaque cohort id (ADR 0015),
// never the raw device token. `mine` flags the caller's own claimed devices
// (Set C) so the map can render them in the local-device accent regardless of
// `visibility`; `visibility` distinguishes public (Set A, exact) from group
// (Set B, coarsened to the device's cohort_precision server-side). No altitude
// and no ping history: the world map plots a single marker per device.
export interface WorldDevice {
  id: string;
  animals: string[];
  visibility: string;
  mine: boolean;
  lat: number;
  lng: number;
  t: number;
  precision_meters: number | null;
  // The device's display tag (ADR 0022); null when unset.
  tag: string | null;
}
export interface WorldSnapshot {
  devices: WorldDevice[];
  since: number;
  now: number;
  truncated: boolean;
}

// Claim system (ADR 0014).
export interface AuthMe {
  id: number;
}
export interface ClaimChallenge {
  nonce: string;
  expires_at: number;
}
export interface ClaimAccepted {
  claimed_at: number;
}
export interface ClaimRevoked {
  revoked_at: number;
}
export interface VisibilitySet {
  visibility: string;
}
export interface ClaimedDevice {
  device_token: string;
  claimed_at: number;
  visibility: string | null;
  // The device's group-key segments (ADR 0025), [] when ungrouped. Drives the
  // SPA's "claim this group" affordance + the group sharing-mode choice. Absent
  // on an older server ⇒ parsed as [].
  group: string[];
}
export interface ClaimList {
  claims: ClaimedDevice[];
}

// Group claim + emergent participation for shared C2 (ADR 0025).
export interface GroupClaim {
  claimed_at: number;
}
export interface GroupClaimRevoked {
  revoked_at: number;
}
// Mint response — the full bearer token is returned ONCE (never re-listed).
export interface GroupInvite {
  invite_token: string;
  expires_at: number | null;
  max_uses: number | null;
}
// A listed invite for owner management: token SUFFIX only (the full bearer token
// is never re-served), plus its usage/lifecycle.
export interface GroupInviteListItem {
  suffix: string;
  created_at: number;
  expires_at: number | null;
  max_uses: number | null;
  uses: number;
}
export interface GroupInviteList {
  invites: GroupInviteListItem[];
}
// One device in the participant-only group console feed. The RAW token (a
// participant is authorized to drive its per-device console/replay), the display
// tag, the full-precision trail, and the command/response history.
export interface GroupConsoleDevice {
  token: string;
  tag: string | null;
  created_at: number;
  pings: Ping[];
  commands: CommandResult[];
}
// A group marker (ADR 0026): a point with a short label (the label doubles as
// low-frequency indirect team messaging) and a small kind id. Group-level only —
// NO person attribution is ever returned (no `created_by_user`; decision 4/5).
export interface Marker {
  id: number;
  lat: number;
  lng: number;
  label: string | null;
  kind: string;
  created_at: number;
  updated_at: number;
}
// A group geofence (ADR 0026): a shared shape (circle xor polygon) a device
// evaluates on-device. Group-level only — no created_by_user is ever returned.
export interface Geofence {
  id: number;
  shape: GeofenceShape;
  label: string | null;
  kind: string;
  created_at: number;
  updated_at: number;
}
// A geofence breach (ADR 0026): a discrete enter/exit a device reported via the
// signed POST /devices/:token/geofence-events. Device-TAG-attributed (never a
// person); `observed_at` is the server clock.
export interface GeofenceEvent {
  geofence_id: number;
  transition: 'enter' | 'exit';
  observed_at: number;
  tag: string | null;
}
export interface GroupConsole {
  group: string[];
  since: number;
  now: number;
  // The caller's relationship to this claimed group (ADR 0025/0027): 'owner' iff
  // they hold the active group claim. Drives the client's group-share UI + overlay
  // owner-gating. Tolerant default 'participant' if an older server omits it.
  role: 'owner' | 'participant';
  devices: GroupConsoleDevice[];
  truncated: boolean;
  // Live group markers + geofence shapes + recent breach events (ADR 0026),
  // additive. Absent ⇒ [].
  markers: Marker[];
  geofences: Geofence[];
  geofence_events: GeofenceEvent[];
}

/* ── primitive validators ───────────────────────────────────────────── */

class WireError extends Error {}

function obj(v: unknown, ctx: string): Record<string, unknown> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) {
    throw new WireError(`${ctx}: expected object`);
  }
  return v as Record<string, unknown>;
}
function num(v: unknown, ctx: string): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) throw new WireError(`${ctx}: expected number`);
  return v;
}
function numOrNull(v: unknown, ctx: string): number | null {
  return v === null ? null : num(v, ctx);
}
// Tolerant string-or-null: an absent key (undefined) reads as null too, so the
// additive `tag` field (ADR 0022) parses cleanly against a server that predates
// it / omits it on a given read.
function strOrNull(v: unknown, ctx: string): string | null {
  return v === null || v === undefined ? null : str(v, ctx);
}
function str(v: unknown, ctx: string): string {
  if (typeof v !== 'string') throw new WireError(`${ctx}: expected string`);
  return v;
}
function arr(v: unknown, ctx: string): unknown[] {
  if (!Array.isArray(v)) throw new WireError(`${ctx}: expected array`);
  return v;
}
function strArray(v: unknown, ctx: string): string[] {
  return arr(v, ctx).map((x, i) => str(x, `${ctx}[${i}]`));
}
function bool(v: unknown, ctx: string): boolean {
  if (typeof v !== 'boolean') throw new WireError(`${ctx}: expected boolean`);
  return v;
}

function parsePing(v: unknown, ctx: string): Ping {
  const o = obj(v, ctx);
  return {
    t: num(o.t, `${ctx}.t`),
    lat: num(o.lat, `${ctx}.lat`),
    lng: num(o.lng, `${ctx}.lng`),
    precision_meters: numOrNull(o.precision_meters, `${ctx}.precision_meters`),
    altitude_meters: numOrNull(o.altitude_meters, `${ctx}.altitude_meters`),
    altitude_accuracy_meters: numOrNull(
      o.altitude_accuracy_meters,
      `${ctx}.altitude_accuracy_meters`,
    ),
    session_id: numOrNull(o.session_id, `${ctx}.session_id`),
    // Per-ping authenticated data (ADR 0023). Owner-read only: the server omits
    // this key on cohort/world/share reads, so `strOrNull` (absent → null) keeps
    // those parses clean; it carries a base64url string on the owner self-read.
    data: strOrNull(o.data, `${ctx}.data`),
  };
}
export function parsePingArray(v: unknown, ctx: string): Ping[] {
  return arr(v, ctx).map((p, i) => parsePing(p, `${ctx}[${i}]`));
}

/* ── response parsers ───────────────────────────────────────────────── */

export function parseDeviceCreated(v: unknown): DeviceCreated {
  const o = obj(v, 'device-created');
  return { token: str(o.token, 'token'), created_at: num(o.created_at, 'created_at') };
}

export function parsePingAccepted(v: unknown): PingAccepted {
  const o = obj(v, 'ping-accepted');
  return { accepted: num(o.accepted, 'accepted') };
}

export function parseDevicePings(v: unknown): DevicePings {
  const o = obj(v, 'device-pings');
  return {
    pings: parsePingArray(o.pings, 'pings'),
    created_at: num(o.created_at, 'created_at'),
    tag: strOrNull(o.tag, 'tag'),
    // Tolerant: an absent key reads as []. Lets an older server (pre-ADR 0024,
    // or any cohort/world/share read that deliberately omits it for the
    // privacy invariant) parse cleanly via the same parser.
    pending_commands:
      o.pending_commands === undefined ? [] : parsePendingCommandArray(o.pending_commands, 'pending_commands'),
    // Same tolerance for the device-key-only geofence piggyback (ADR 0026):
    // absent on every non-device-key read ⇒ [].
    geofences: o.geofences === undefined ? [] : parseGeofenceArray(o.geofences, 'geofences'),
  };
}

function parsePendingCommand(v: unknown, ctx: string): PendingCommand {
  const o = obj(v, ctx);
  const tier = str(o.tier, `${ctx}.tier`);
  if (tier !== 'query' && tier !== 'command') {
    throw new WireError(`${ctx}.tier: expected 'query' | 'command'`);
  }
  return {
    id: num(o.id, `${ctx}.id`),
    tier,
    kind: str(o.kind, `${ctx}.kind`),
    args: strOrNull(o.args, `${ctx}.args`),
  };
}
export function parsePendingCommandArray(v: unknown, ctx: string): PendingCommand[] {
  return arr(v, ctx).map((p, i) => parsePendingCommand(p, `${ctx}[${i}]`));
}

export function parseCapabilitiesSet(v: unknown): CapabilitiesSet {
  const o = obj(v, 'capabilities-set');
  const c = obj(o.capabilities, 'capabilities');
  return {
    capabilities: {
      query: bool(c.query, 'capabilities.query'),
      command: bool(c.command, 'capabilities.command'),
    },
  };
}

export function parseCommandEnqueued(v: unknown): CommandEnqueued {
  const o = obj(v, 'command-enqueued');
  return { id: num(o.id, 'id'), expires_at: num(o.expires_at, 'expires_at') };
}

export function parseCommandResultAccepted(v: unknown): CommandResultAccepted {
  const o = obj(v, 'command-result-accepted');
  return { acked_at: num(o.acked_at, 'acked_at') };
}

function parseCommandResult(v: unknown, ctx: string): CommandResult {
  const o = obj(v, ctx);
  const tier = str(o.tier, `${ctx}.tier`);
  if (tier !== 'query' && tier !== 'command') {
    throw new WireError(`${ctx}.tier: expected 'query' | 'command'`);
  }
  return {
    id: num(o.id, `${ctx}.id`),
    tier,
    kind: str(o.kind, `${ctx}.kind`),
    args: strOrNull(o.args, `${ctx}.args`),
    issued_at: num(o.issued_at, `${ctx}.issued_at`),
    expires_at: num(o.expires_at, `${ctx}.expires_at`),
    acked_at: numOrNull(o.acked_at, `${ctx}.acked_at`),
    result_data: strOrNull(o.result_data, `${ctx}.result_data`),
  };
}

// `capabilities` is null when the device never advertised; otherwise the two
// required booleans. Tolerant of an absent `commands` key (parses as []).
export function parseDeviceCommandsView(v: unknown): DeviceCommandsView {
  const o = obj(v, 'device-commands-view');
  let capabilities: DeviceCapabilities | null = null;
  if (o.capabilities !== null && o.capabilities !== undefined) {
    const c = obj(o.capabilities, 'capabilities');
    capabilities = {
      query: bool(c.query, 'capabilities.query'),
      command: bool(c.command, 'capabilities.command'),
    };
  }
  return {
    capabilities,
    commands:
      o.commands === undefined
        ? []
        : arr(o.commands, 'commands').map((c, i) => parseCommandResult(c, `commands[${i}]`)),
  };
}

export function parseDeviceTagSet(v: unknown): DeviceTagSet {
  const o = obj(v, 'device-tag-set');
  return { tag: strOrNull(o.tag, 'tag') };
}

export function parseSessionStarted(v: unknown): SessionStarted {
  const o = obj(v, 'session-started');
  return { session_id: num(o.session_id, 'session_id'), started_at: num(o.started_at, 'started_at') };
}

export function parseSessionStopped(v: unknown): SessionStopped {
  const o = obj(v, 'session-stopped');
  return { session_id: num(o.session_id, 'session_id'), ended_at: num(o.ended_at, 'ended_at') };
}

export function parseShareMinted(v: unknown): ShareMinted {
  const o = obj(v, 'share-minted');
  return { share_token: str(o.share_token, 'share_token'), expires_at: numOrNull(o.expires_at, 'expires_at') };
}

export function parseShareMeta(v: unknown): ShareMeta {
  const o = obj(v, 'share-meta');
  return {
    share_token: str(o.share_token, 'share_token'),
    range: str(o.range, 'range'),
    precision: str(o.precision, 'precision'),
    expires_at: numOrNull(o.expires_at, 'expires_at'),
    created_at: num(o.created_at, 'created_at'),
  };
}

export function parseSharePings(v: unknown): SharePings {
  const o = obj(v, 'share-pings');
  const scope = obj(o.share_scope, 'share_scope');
  return {
    pings: parsePingArray(o.pings, 'pings'),
    share_scope: {
      range: str(scope.range, 'share_scope.range'),
      precision: str(scope.precision, 'share_scope.precision'),
      expires_at: numOrNull(scope.expires_at, 'share_scope.expires_at'),
    },
    since: num(o.since, 'since'),
    now: num(o.now, 'now'),
  };
}

export function parseAuthMe(v: unknown): AuthMe {
  const o = obj(v, 'auth-me');
  return { id: num(o.id, 'id') };
}

export function parseClaimChallenge(v: unknown): ClaimChallenge {
  const o = obj(v, 'claim-challenge');
  return { nonce: str(o.nonce, 'nonce'), expires_at: num(o.expires_at, 'expires_at') };
}

export function parseClaimAccepted(v: unknown): ClaimAccepted {
  const o = obj(v, 'claim-accepted');
  return { claimed_at: num(o.claimed_at, 'claimed_at') };
}

export function parseClaimRevoked(v: unknown): ClaimRevoked {
  const o = obj(v, 'claim-revoked');
  return { revoked_at: num(o.revoked_at, 'revoked_at') };
}

export function parseVisibilitySet(v: unknown): VisibilitySet {
  const o = obj(v, 'visibility-set');
  return { visibility: str(o.visibility, 'visibility') };
}

export function parseClaimList(v: unknown): ClaimList {
  const o = obj(v, 'claim-list');
  return {
    claims: arr(o.claims, 'claims').map((c, i) => {
      const d = obj(c, `claims[${i}]`);
      return {
        device_token: str(d.device_token, `claims[${i}].device_token`),
        claimed_at: num(d.claimed_at, `claims[${i}].claimed_at`),
        visibility: d.visibility === null ? null : str(d.visibility, `claims[${i}].visibility`),
        // Tolerant: an absent key (older server) reads as [].
        group: d.group === undefined ? [] : strArray(d.group, `claims[${i}].group`),
      };
    }),
  };
}

export function parseGroupClaim(v: unknown): GroupClaim {
  const o = obj(v, 'group-claim');
  return { claimed_at: num(o.claimed_at, 'claimed_at') };
}

export function parseGroupClaimRevoked(v: unknown): GroupClaimRevoked {
  const o = obj(v, 'group-claim-revoked');
  return { revoked_at: num(o.revoked_at, 'revoked_at') };
}

export function parseGroupInvite(v: unknown): GroupInvite {
  const o = obj(v, 'group-invite');
  return {
    invite_token: str(o.invite_token, 'invite_token'),
    expires_at: numOrNull(o.expires_at, 'expires_at'),
    max_uses: numOrNull(o.max_uses, 'max_uses'),
  };
}

export function parseGroupInviteList(v: unknown): GroupInviteList {
  const o = obj(v, 'group-invite-list');
  return {
    invites: arr(o.invites, 'invites').map((it, i) => {
      const d = obj(it, `invites[${i}]`);
      return {
        suffix: str(d.suffix, `invites[${i}].suffix`),
        created_at: num(d.created_at, `invites[${i}].created_at`),
        expires_at: numOrNull(d.expires_at, `invites[${i}].expires_at`),
        max_uses: numOrNull(d.max_uses, `invites[${i}].max_uses`),
        uses: num(d.uses, `invites[${i}].uses`),
      };
    }),
  };
}

export function parseMarker(v: unknown, ctx: string): Marker {
  const o = obj(v, ctx);
  return {
    id: num(o.id, `${ctx}.id`),
    lat: num(o.lat, `${ctx}.lat`),
    lng: num(o.lng, `${ctx}.lng`),
    label: strOrNull(o.label, `${ctx}.label`),
    kind: str(o.kind, `${ctx}.kind`),
    created_at: num(o.created_at, `${ctx}.created_at`),
    updated_at: num(o.updated_at, `${ctx}.updated_at`),
  };
}
export function parseMarkerArray(v: unknown, ctx: string): Marker[] {
  return arr(v, ctx).map((m, i) => parseMarker(m, `${ctx}[${i}]`));
}

export function parseGeofence(v: unknown, ctx: string): Geofence {
  const o = obj(v, ctx);
  const shape = parseGeofenceShape(o.shape);
  if (!shape) throw new WireError(`${ctx}.shape: invalid geofence shape`);
  return {
    id: num(o.id, `${ctx}.id`),
    shape,
    label: strOrNull(o.label, `${ctx}.label`),
    kind: str(o.kind, `${ctx}.kind`),
    created_at: num(o.created_at, `${ctx}.created_at`),
    updated_at: num(o.updated_at, `${ctx}.updated_at`),
  };
}
export function parseGeofenceArray(v: unknown, ctx: string): Geofence[] {
  return arr(v, ctx).map((g, i) => parseGeofence(g, `${ctx}[${i}]`));
}

export function parseGeofenceEvent(v: unknown, ctx: string): GeofenceEvent {
  const o = obj(v, ctx);
  const transition = str(o.transition, `${ctx}.transition`);
  if (transition !== 'enter' && transition !== 'exit') {
    throw new WireError(`${ctx}.transition: expected 'enter' | 'exit'`);
  }
  return {
    geofence_id: num(o.geofence_id, `${ctx}.geofence_id`),
    transition,
    observed_at: num(o.observed_at, `${ctx}.observed_at`),
    tag: strOrNull(o.tag, `${ctx}.tag`),
  };
}
export function parseGeofenceEventArray(v: unknown, ctx: string): GeofenceEvent[] {
  return arr(v, ctx).map((e, i) => parseGeofenceEvent(e, `${ctx}[${i}]`));
}

export function parseGroupConsole(v: unknown): GroupConsole {
  const o = obj(v, 'group-console');
  return {
    group: strArray(o.group, 'group'),
    since: num(o.since, 'since'),
    now: num(o.now, 'now'),
    // Tolerant: absent (older server) or unrecognised ⇒ 'participant'. Fail-safe —
    // never grant owner affordances off a stale/garbled field.
    role: o.role === 'owner' ? 'owner' : 'participant',
    devices: arr(o.devices, 'devices').map((d, i) => {
      const dev = obj(d, `devices[${i}]`);
      return {
        token: str(dev.token, `devices[${i}].token`),
        tag: strOrNull(dev.tag, `devices[${i}].tag`),
        created_at: num(dev.created_at, `devices[${i}].created_at`),
        pings: parsePingArray(dev.pings, `devices[${i}].pings`),
        commands:
          dev.commands === undefined
            ? []
            : arr(dev.commands, `devices[${i}].commands`).map((c, j) =>
                parseCommandResult(c, `devices[${i}].commands[${j}]`),
              ),
      };
    }),
    truncated: bool(o.truncated, 'truncated'),
    // Tolerant: absent (older server) ⇒ [].
    markers: o.markers === undefined ? [] : parseMarkerArray(o.markers, 'markers'),
    geofences: o.geofences === undefined ? [] : parseGeofenceArray(o.geofences, 'geofences'),
    geofence_events:
      o.geofence_events === undefined ? [] : parseGeofenceEventArray(o.geofence_events, 'geofence_events'),
  };
}

export function parseCohortScopeSet(v: unknown): CohortScopeSet {
  const o = obj(v, 'cohort-scope-set');
  const scope = obj(o.cohort_scope, 'cohort_scope');
  return {
    cohort_scope: {
      range: str(scope.range, 'cohort_scope.range'),
      precision: str(scope.precision, 'cohort_scope.precision'),
    },
  };
}

export function parseWorldSnapshot(v: unknown): WorldSnapshot {
  const o = obj(v, 'world-snapshot');
  return {
    devices: arr(o.devices, 'devices').map((d, i) => {
      const dev = obj(d, `devices[${i}]`);
      return {
        id: str(dev.id, `devices[${i}].id`),
        animals: strArray(dev.animals, `devices[${i}].animals`),
        visibility: str(dev.visibility, `devices[${i}].visibility`),
        mine: bool(dev.mine, `devices[${i}].mine`),
        lat: num(dev.lat, `devices[${i}].lat`),
        lng: num(dev.lng, `devices[${i}].lng`),
        t: num(dev.t, `devices[${i}].t`),
        precision_meters: numOrNull(dev.precision_meters, `devices[${i}].precision_meters`),
        tag: strOrNull(dev.tag, `devices[${i}].tag`),
      };
    }),
    since: num(o.since, 'since'),
    now: num(o.now, 'now'),
    truncated: bool(o.truncated, 'truncated'),
  };
}

export function parseGroupRecent(v: unknown): GroupRecent {
  const o = obj(v, 'group-recent');
  return {
    group: strArray(o.group, 'group'),
    since: num(o.since, 'since'),
    now: num(o.now, 'now'),
    devices: arr(o.devices, 'devices').map((d, i) => {
      const dev = obj(d, `devices[${i}]`);
      const scope = obj(dev.cohort_scope, `devices[${i}].cohort_scope`);
      return {
        token: str(dev.token, `devices[${i}].token`),
        animals: strArray(dev.animals, `devices[${i}].animals`),
        created_at: num(dev.created_at, `devices[${i}].created_at`),
        pings: parsePingArray(dev.pings, `devices[${i}].pings`),
        truncated: bool(dev.truncated, `devices[${i}].truncated`),
        cohort_scope: {
          range: str(scope.range, `devices[${i}].cohort_scope.range`),
          precision: str(scope.precision, `devices[${i}].cohort_scope.precision`),
        },
        tag: strOrNull(dev.tag, `devices[${i}].tag`),
      };
    }),
    truncated: bool(o.truncated, 'truncated'),
  };
}
